/* jshint esversion: 6 */
/* jshint esversion: 8 */
/* jshint ignore:start */
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { FiGift } from "react-icons/fi";
import { MyContext } from "../Context/MyProvider";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import LodingLottie from "../Lodinglottie";
import { showTost } from "../showTost";
import CoinIcon from "../images/icons/buycoin-package.png";

import PageHeader from "./PageHeader";

const MyGift = () => {
  const { t } = useTranslation();
  const { basUrl, imageBaseURL, currency } = useContext(MyContext);
  const [gifts, setGifts] = useState([]);
  const [giftCurrency, setGiftCurrency] = useState(currency || "$");
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const fetchingRef = useRef(false);

  const getUserId = () => {
    try {
      return JSON.parse(localStorage.getItem("Register_User") || "{}").id;
    } catch {
      return null;
    }
  };

  const imageUrl = (path) => {
    const value = String(path || "").trim();
    if (!value) return "";
    if (/^https?:\/\//i.test(value)) return value;
    return `${imageBaseURL}${value.replace(/^\/+/, "")}`;
  };

  const getGiftImages = useCallback((gift) => {
    return String(gift?.gift_img || "")
      .split(/[;,]/)
      .map((item) => item.trim())
      .filter(Boolean);
  }, []);

  const normalizeGiftKey = useCallback((path) => {
    return String(path || "")
      .trim()
      .replace(/^https?:\/\/[^/]+\//i, "")
      .replace(/^\/+/, "")
      .toLowerCase();
  }, []);

  const combineGiftRows = useCallback((rows = [], coinRate = 0) => {
    const grouped = new Map();
    const rate = Number(coinRate || 0);

    rows.forEach((gift) => {
      const giftImages = getGiftImages(gift);
      const images = giftImages.length > 0 ? giftImages : [gift?.gift_img].filter(Boolean);
      const rowCount = Number(gift?.count || 1);
      const rowTotalCoin = Number(gift?.total_coin || 0);
      const rowTotalEarning = gift?.total_earning !== undefined && gift?.total_earning !== null
        ? Number(gift.total_earning || 0)
        : rowTotalCoin * rate;
      const rowSenderCoin = Number(gift?.sender_total_coin || rowTotalCoin || 0);
      const rowSenderEarning = gift?.sender_total_earning !== undefined && gift?.sender_total_earning !== null
        ? Number(gift.sender_total_earning || 0)
        : rowSenderCoin * rate;
      const perGiftCoin = images.length > 0 ? rowTotalCoin / images.length : rowTotalCoin;
      const perGiftEarning = images.length > 0 ? rowTotalEarning / images.length : rowTotalEarning;

      images.forEach((giftImage) => {
        const senderKey = gift?.sender_id || gift?.senderId || gift?.name || gift?.img || "";
        const key = `${senderKey}|${normalizeGiftKey(giftImage)}`;
        const existing = grouped.get(key);

        if (existing) {
          existing.count = Number(existing.count || 0) + rowCount;
          existing.total_coin = Number(existing.total_coin || 0) + perGiftCoin;
          existing.total_earning = Number(existing.total_earning || 0) + perGiftEarning;
          existing.sender_total_coin = Math.max(Number(existing.sender_total_coin || 0), rowSenderCoin);
          existing.sender_total_earning = Math.max(Number(existing.sender_total_earning || 0), rowSenderEarning);
        } else {
          grouped.set(key, {
            ...gift,
            gift_img: giftImage,
            count: rowCount,
            total_coin: perGiftCoin,
            total_earning: perGiftEarning,
            sender_total_coin: rowSenderCoin || perGiftCoin,
            sender_total_earning: rowSenderEarning || perGiftEarning,
          });
        }
      });
    });

    const senderTotals = new Map();
    const senderEarnings = new Map();
    grouped.forEach((gift) => {
      const senderKey = gift?.sender_id || gift?.senderId || gift?.name || gift?.img || "";
      senderTotals.set(senderKey, (senderTotals.get(senderKey) || 0) + Number(gift.total_coin || 0));
      senderEarnings.set(senderKey, (senderEarnings.get(senderKey) || 0) + Number(gift.total_earning || 0));
    });

    return Array.from(grouped.values()).map((gift) => ({
      ...gift,
      count: String(Math.round(Number(gift.count || 0))),
      total_coin: String(Math.round(Number(gift.total_coin || 0))),
      total_earning: String(Number(Number(gift.total_earning || 0).toFixed(2))),
      sender_total_coin: String(Math.round(Math.max(
        Number(gift.sender_total_coin || 0),
        senderTotals.get(gift?.sender_id || gift?.senderId || gift?.name || gift?.img || "") || 0
      ))),
      sender_total_earning: String(Number(Math.max(
        Number(gift.sender_total_earning || 0),
        senderEarnings.get(gift?.sender_id || gift?.senderId || gift?.name || gift?.img || "") || 0
      ).toFixed(2))),
    }));
  }, [getGiftImages, normalizeGiftKey]);

  const formatAmount = (value) => {
    const amount = Number(value || 0);
    return Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
  };

  const fetchMyGift = useCallback((showLoader = true) => {
    if (fetchingRef.current) return;

    const uid = getUserId();
    if (!uid) {
      setLoading(false);
      setErrorMessage("Login required");
      showTost({ title: "Login required" });
      return;
    }

    fetchingRef.current = true;
    setErrorMessage("");
    if (showLoader) {
      setLoading(true);
    }

    axios
      .post(`${basUrl}my_gift`, { uid })
      .then((res) => {
        if (res.data.Result === "true") {
          const responseRate = Number(res.data.coin_rate || 0);
          setGiftCurrency(res.data.currency || currency || "$");
          setGifts(combineGiftRows(res.data.giflist || [], responseRate));
        } else {
          setGifts([]);
          setErrorMessage(res.data.ResponseMsg || "Unable to load gifts.");
          if (res.data.ResponseMsg) {
            showTost({ title: res.data.ResponseMsg });
          }
        }
      })
      .catch((error) => {
        console.error("Error fetching my gifts:", error);
        showTost({ title: "Something Went Wrong!" });
        setErrorMessage("Unable to load gifts. Please try again.");
        setGifts([]);
      })
      .finally(() => {
        fetchingRef.current = false;
        setLoading(false);
      });
  }, [basUrl, combineGiftRows, currency]);

  useEffect(() => {
    fetchMyGift();
    const handleGiftReceived = () => fetchMyGift(false);
    window.addEventListener("gomeet:gift-received", handleGiftReceived);
    return () => window.removeEventListener("gomeet:gift-received", handleGiftReceived);
  }, [fetchMyGift]);

  return (
    <div className="main-wrapper bg-[#e5e5e5] dashboard">
      <div className="content-body">
        <div className="container-fluid my-2 sm:my-4 px-2.5 sm:px-4">
          <PageHeader
            title={t("My Gift")}
            subtitle={t("Gifts you received from other users")}
            breadcrumbs={[{ label: t("My Gift") }]}
            icon={FiGift}
            actions={
              <div className="flex items-center gap-2 flex-wrap">
                <Link
                  to="/history"
                  className="px-4 py-2 rounded-xl font-semibold text-sm border border-purple-200 text-purple-700 bg-purple-50 hover:bg-purple-100 transition-all shadow-xs"
                >
                  {t("History")}
                </Link>
                <Link
                  to="/buycoin?withdraw=1"
                  className="px-4 py-2 rounded-xl font-semibold text-sm text-white bg-purple-600 hover:bg-purple-700 transition-all shadow-xs"
                >
                  {t("Withdraw")}
                </Link>
                <button
                  onClick={fetchMyGift}
                  className="px-4 py-2 rounded-xl font-semibold text-sm border border-purple-200 text-purple-700 bg-purple-50 hover:bg-purple-100 transition-all shadow-xs cursor-pointer"
                >
                  {t("Refresh")}
                </button>
              </div>
            }
          />

          <div className="bg-white rounded-[10px] p-[20px] min-h-[520px]">
            {loading ? (
              <div className="h-[420px] flex flex-col items-center justify-center">
                <LodingLottie ClassCss="w-[82px] h-[82px]" />
                <p className="m-0 mt-[10px] text-gray-500">{t("Loading...")}</p>
              </div>
            ) : errorMessage ? (
              <div className="h-[420px] flex flex-col items-center justify-center text-center">
                <div className="w-[76px] h-[76px] rounded-full bg-[#f5e8ff] flex items-center justify-center mb-[18px]">
                  <FiGift className="text-[rgba(152,14,255,255)] w-[36px] h-[36px]" />
                </div>
                <h4 className="mb-[6px]">{t("My Gift")}</h4>
                <p className="m-0 text-gray-500">{errorMessage}</p>
                <button
                  onClick={() => fetchMyGift(true)}
                  className="mt-[18px] bg-[rgba(152,14,255,255)] text-white rounded-full px-[22px] py-[9px] font-[600]"
                >
                  {t("Refresh")}
                </button>
              </div>
            ) : gifts.length > 0 ? (
              <div className="grid grid-cols-4 max-_1500_:grid-cols-3 max-_1030_:grid-cols-2 max-_768_:grid-cols-2 max-_430_:grid-cols-1 gap-[20px]">
                {gifts.map((gift, index) => {
                  const giftImages = getGiftImages(gift);
                  const visibleGiftImages = giftImages.slice(0, 3);
                  const senderImage = imageUrl(gift.img) || imageUrl(giftImages[0]);
                  const giftCount = Number(gift.count || giftImages.length || 1);
                  const earnedCoin = Number(gift.total_coin || 0);
                  const earnedAmount = Number(gift.total_earning || 0);
                  const previewWidth = visibleGiftImages.length > 0
                    ? 72 + ((visibleGiftImages.length - 1) * 30)
                    : 72;

                  return (
                    <div key={`${gift.gift_img}-${gift.img}-${index}`} className="border border-[#eadcf5] rounded-[10px] p-[16px] bg-white">
                      <div className="relative rounded-[10px] bg-[#f8efff] h-[132px] flex items-center justify-center overflow-hidden">
                        {visibleGiftImages.length > 0 ? (
                          <div className="relative h-[88px]" style={{ width: `${previewWidth}px` }}>
                            {visibleGiftImages.map((giftImage, giftIndex) => (
                              <div
                                key={`${giftImage}-${giftIndex}`}
                                className="absolute top-1/2 w-[72px] h-[72px] rounded-full bg-white shadow-sm border border-[#efdfff] flex items-center justify-center"
                                style={{
                                  left: `${giftIndex * 30}px`,
                                  transform: "translateY(-50%)",
                                  zIndex: visibleGiftImages.length - giftIndex,
                                }}
                              >
                                <img
                                  src={imageUrl(giftImage)}
                                  className="max-w-[48px] max-h-[48px] object-contain"
                                  alt={t("Gift")}
                                />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="w-[72px] h-[72px] rounded-full bg-white shadow-sm border border-[#efdfff] flex items-center justify-center">
                            <FiGift className="text-[rgba(152,14,255,255)] w-[34px] h-[34px]" />
                          </div>
                        )}
                        <span className="absolute top-[12px] right-[12px] bg-[rgba(152,14,255,255)] text-white text-[13px] rounded-full min-w-[30px] h-[30px] px-[8px] flex items-center justify-center font-[700]">
                          {giftCount}
                        </span>
                        {giftImages.length > visibleGiftImages.length && (
                          <span className="absolute bottom-[12px] right-[12px] bg-white text-[rgba(152,14,255,255)] text-[12px] rounded-full px-[9px] py-[4px] font-[700] shadow-sm">
                            +{giftImages.length - visibleGiftImages.length} more
                          </span>
                        )}
                      </div>

                      <div className="flex items-center justify-between gap-[12px] mt-[16px]">
                        <div className="flex items-center gap-[12px] min-w-0">
                        {senderImage ? (
                          <img
                            src={senderImage}
                            className="w-[44px] h-[44px] rounded-full object-cover bg-[#f1f1f1]"
                            alt={gift.name || t("User")}
                          />
                        ) : (
                          <div className="w-[44px] h-[44px] rounded-full bg-[#f5e8ff] flex items-center justify-center">
                            <FiGift className="text-[rgba(152,14,255,255)] w-[22px] h-[22px]" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <h6 className="m-0 text-[16px] font-[700] truncate">{gift.name || t("Unknown User")}</h6>
                          <p className="m-0 text-gray-500 text-[14px]">{t("Sent you a gift")}</p>
                        </div>
                        </div>
                        <div className="text-end shrink-0">
                          <h6 className="m-0 text-[16px] font-[800] text-[rgba(152,14,255,255)]">+{giftCurrency}{formatAmount(earnedAmount)}</h6>
                          <p className="m-0 text-[12px] text-gray-500 flex items-center justify-end gap-[4px]">
                            <img src={CoinIcon} className="w-[13px] h-[13px] object-contain" alt={t("coins")} />
                            <span>{earnedCoin} {t("coins")}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="h-[420px] flex flex-col items-center justify-center text-center">
                <div className="w-[76px] h-[76px] rounded-full bg-[#f5e8ff] flex items-center justify-center mb-[18px]">
                  <FiGift className="text-[rgba(152,14,255,255)] w-[36px] h-[36px]" />
                </div>
                <h4 className="mb-[6px]">{t("No Gifts Available")}</h4>
                <p className="m-0 text-gray-500">{t("There are no gifts available in your account.")}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyGift;
/* jshint ignore:end */
